import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { HistoryOrder } from '~Scenes/History';
import useI18n from '~Translations';
import { BlackButton, BlueButton, Box, WhiteButton } from '~Styles';
import SelectRefundArticles from './SelectArticles';
import Summary from './Summary';
import { useRefunds } from '~Components/Refunds/Provider';
import InProgress from './InProgress';
import { useButton, useDispatch, useLogger, useSelector } from '~Hooks';
import * as actions from '~actions';
import TipRefund from './TipRefund';
import Button from '~ComponentsImproved/Button';

const Wrapper = styled.div`
  background-color: var(--colorBlueGray1200);
  width: 100%;
  height: 100%;
  color: var(--colorWhite);

  @media screen and (min-width: 600px) {
    width: 95%;
    height: 95%;
    max-width: 700px;
    max-height: 700px;
    border-radius: 4px;
  }
`;

const InnerWrapper = styled.div`
  height: 100%;
  padding: 20px;
  box-sizing: border-box;

  @media screen and (min-width: 600px) {
    padding: 32px;
  }
`;

type ItemType = {
  id: string;
  title: string;
  price: number;
  quantity: number;
  vatRate: number;
  categoryId: string;
  categoryName: string;
  discount: number;
};

type Steps = 'selectRefundType' | 'selectItems' | 'refundTips' | 'summary' | 'in-progress';

interface Props {
  orderId: string;
  selectedOrder: HistoryOrder | any; //TODO: We need to make a general history query so we can use this for both history and handy history
  closeModal: () => void;
  onRefundCompleted?: () => void;
}

const Refunds: FC<Props> = ({ orderId, selectedOrder, closeModal, onRefundCompleted }) => {
  const dispatch = useDispatch();
  const country = useSelector((state) => state.country);
  const [refundType, setRefundType] = useState<'ITEM' | 'ORDER' | 'TIP' | null>(null);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const refunds = useRefunds();
  const { i18n } = useI18n();
  const [newTipAmount, setNewTipAmount] = useState<number | null>(null);
  // Array to make it easier to navigate back
  const [steps, setSteps] = useState<Steps[]>([]);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const currentStep = steps[steps.length - 1];
  const refundableItems = [...selectedOrder.items].filter((item) => !item.isRefunded);
  const orderHasTips = selectedOrder.tip > 0 || selectedOrder.payment.correctedTip > 0;
  const logger = useLogger('refunds');

  const isInvoicePayment = selectedOrder.payment.type === 'INVOICE';
  const allowPartialInvoiceRefund = country === 'NO';
  const isMultiPayment =
    selectedOrder.payment.subPayments && selectedOrder.payment.subPayments.length > 1;

  useEffect(() => {
    if (
      (refundableItems.length === 1 && !orderHasTips) ||
      // Only allow full refund for invoice payments, in all countries except NO
      (isInvoicePayment && !allowPartialInvoiceRefund)
    ) {
      // If the order only have 1 item and no tips, go straight to summary and skip the select refund type view
      setSteps((prev) => [...prev, 'summary']);
      setRefundType('ORDER');
    } else if (refundableItems.length === 0 && orderHasTips) {
      // If there are no items that can be refunded, go straight to refund tips
      setRefundType('TIP');

      setSteps((prev) => [...prev, 'refundTips']);
    } else {
      setSteps((prev) => [...prev, 'selectRefundType']);
    }
  }, []);

  const handleToggle = (item: any, isActive: boolean) => {
    if (isActive) {
      setSelectedItems((prev) => [...prev, item]);
    } else {
      setSelectedItems((prev) => prev.filter((prevItem) => prevItem.id !== item.id));
    }
  };

  const goBack = () => {
    if (steps.length === 1) {
      closeModal();
    } else {
      setSteps((prev) => prev.slice(0, -1));
    }
  };

  useEffect(() => {
    const latestStep = steps[steps.length - 1];

    if (latestStep === 'selectRefundType') {
      setSelectedItems([]);
      setNewTipAmount(null);
    }
  }, [steps]);

  function handleSubmit() {
    logger.info('Refund button pressed');

    if (!refundType) {
      logger.error('Refund button pressed but no refund type was selected'); // Should not happen
      return;
    }

    const baseValues = {
      method: selectedOrder.payment.type,
      orderId: orderId,
      origin: selectedOrder.origin,
      transactionCreatedAt: selectedOrder.payment.createdAt,
      transactionId: selectedOrder.payment.transactionId,
      waiterId: selectedOrder?.staff?.id,
      onCompleted: (receiptErrorMessage?: string) => {
        onRefundCompleted && onRefundCompleted();
        setIsCompleted(true);

        if (receiptErrorMessage) {
          // Refund succeeded but receipt failed
          setReceiptError(receiptErrorMessage);
          logger.warn('Refund completed but receipt generation failed', {
            orderId,
            refundType,
            selectedItems,
            newTipAmount,
            receiptError: receiptErrorMessage,
          });

          // Don't close modal - show receipt error state with retry option
          let message = i18n('History.RefundSuccessful');
          if (refundType === 'TIP') {
            message = i18n('History.TipRefundCompleted');
          }
          // Show success toast but keep modal open for receipt retry
          dispatch(actions.addToast(message + ' - ' + i18n('History.ReceiptFailed'), true));
        } else {
          // Everything succeeded including receipt
          closeModal();

          let message = i18n('History.RefundSuccessful');
          if (refundType === 'TIP') {
            message = i18n('History.TipRefundCompleted');
          }

          dispatch(actions.addToast(message, true));
          logger.info('Refund completed', {
            orderId,
            refundType,
            selectedItems,
            newTipAmount,
          });
        }
      },
      onFail: (error?: string) => {
        setRefundError(error || 'Error');
        closeModal();
        dispatch(actions.addToast(error || i18n('Error.SomethingWrong'), true, null, true));
        logger.error('Refund error', {
          orderId,
          refundType,
          selectedItems,
          newTipAmount,
        });
      },
    };

    const items = (refundType === 'ORDER' ? refundableItems : selectedItems).map((i) => ({
      amount: i.price.amount,
      entryId: i.entry.id,
      fraction: i.fraction,
      title: i18n(i.entry.item.titleLang),
      categoryId: i.entry.item.categories[0].id,
      categoryName: i.entry.item.categories[0].name,
      discount: i.price.discount,
      id: i.id,
      itemId: i.entry.item.id,
      vatRate: i.price.percentageVAT,
    }));

    if (refundType === 'TIP') {
      refunds.add({
        type: refundType,
        ...baseValues,
        newTipAmount: newTipAmount!,
        previousTipAmount: selectedOrder.payment.correctedTip,
      });
    } else if (refundType === 'ORDER') {
      refunds.add({
        type: refundType,
        ...baseValues,
        tipAmount: selectedOrder.payment.correctedTip,
        items,
      });
    } else if (refundType === 'ITEM') {
      refunds.add({
        type: refundType,
        ...baseValues,
        items,
      });
    }
  }

  const next = () => {
    if (currentStep == 'summary') {
      handleSubmit();
      setSteps((prev) => [...prev, 'in-progress']);
    } else {
      setSteps((prev) => [...prev, 'summary']);
    }
  };

  function navigateTo(step: Steps) {
    setSteps((prev) => [...prev, step]);
  }

  let refundStatus: string | null = null;

  if (isCompleted && !receiptError) {
    refundStatus = 'COMPLETED';
  } else if (refundError) {
    refundStatus = 'ERROR';
  } else if (refunds.hasRefundInProgress) {
    refundStatus = 'PENDING';
  }

  const refundFullOrderButton = useButton({
    onClick: () => {
      navigateTo('summary');
      setRefundType('ORDER');
    },
  });

  const refundSelectedItemsButton = useButton({
    onClick: () => {
      navigateTo('selectItems');
      setRefundType('ITEM');
    },
    disabled: isMultiPayment,
    disabledToast: isMultiPayment ? i18n('Refunds.Disabled.MultiPayment.Partial') : undefined,
  });

  const refundTipsButton = useButton({
    onClick: () => {
      navigateTo('refundTips');
      setRefundType('TIP');
    },
  });

  const prevButton = useButton({
    onClick: goBack,
  });

  const okButton = useButton({
    onClick: next,
    disabled:
      (currentStep === 'selectItems' && selectedItems.length === 0) ||
      (currentStep === 'refundTips' &&
        (newTipAmount === null || newTipAmount === selectedOrder.payment.correctedTip)),
  });

  // Show receipt error state
  const showReceiptError = isCompleted && receiptError;

  return (
    <Wrapper>
      <InnerWrapper>
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection="column"
          style={{ height: '100%' }}>
          {showReceiptError && (
            <>
              <Box display="flex" flexDirection="column" style={{ gap: '16px', flex: 1 }}>
                <Box
                  style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    marginBottom: '16px',
                  }}>
                  ✓ {i18n('History.RefundSuccessful')}
                </Box>
                <Box
                  style={{
                    fontSize: '18px',
                    color: 'var(--colorUIRed)',
                    textAlign: 'center',
                    marginBottom: '24px',
                  }}>
                  {i18n('History.ReceiptFailed') || 'Receipt generation failed'}
                </Box>
                <Box
                  style={{
                    fontSize: '14px',
                    textAlign: 'center',
                    opacity: 0.8,
                  }}>
                  {receiptError}
                </Box>
              </Box>
              <Box display="flex" style={{ gap: '16px' }} marginTop="medium">
                <Button
                  styleType="Gray"
                  text={i18n('History.Close') || 'Close'}
                  onTouchEnd={closeModal}
                  style={{
                    flex: 1,
                  }}
                />
                <Button
                  styleType="OK"
                  text={i18n('History.PrintFromHistory') || 'Print from History'}
                  onTouchEnd={closeModal}
                  style={{
                    flex: 2,
                  }}
                />
              </Box>
            </>
          )}

          {!showReceiptError && currentStep === 'selectRefundType' && (
            <Box display="flex" flexDirection="column" style={{ gap: '16px' }}>
              <WhiteButton {...refundFullOrderButton}>
                {i18n('Refunds.RefundFullOrder.Button')}
              </WhiteButton>
              {!isInvoicePayment && refundableItems.length > 1 && (
                <WhiteButton {...refundSelectedItemsButton}>
                  {i18n('Refunds.SelectItems.Button')}
                </WhiteButton>
              )}

              {orderHasTips && (
                <WhiteButton {...refundTipsButton}>
                  {i18n('Refunds.CorrectTip.Button')}
                </WhiteButton>
              )}
            </Box>
          )}

          {!showReceiptError && currentStep === 'selectItems' && (
            <SelectRefundArticles
              list={refundableItems}
              selectedItems={selectedItems}
              handleToggle={handleToggle}
            />
          )}

          {!showReceiptError && currentStep === 'refundTips' && (
            <TipRefund
              currentTip={selectedOrder.payment.correctedTip}
              newTipAmount={newTipAmount}
              setNewTipAmount={setNewTipAmount}
            />
          )}

          {!showReceiptError && currentStep === 'summary' && (
            <Summary
              type={refundType!}
              amount={selectedOrder.payment.correctedTip - newTipAmount!}
              selectedItems={refundType === 'ORDER' ? refundableItems : selectedItems}
              tipAmount={selectedOrder.payment.correctedTip}
            />
          )}

          {!showReceiptError && currentStep === 'in-progress' ? (
            <InProgress status={refundStatus as any} error={refundError} onRetry={handleSubmit} />
          ) : (
            !showReceiptError && (
              <Box display="flex" style={{ gap: '16px' }} marginTop="medium">
                <BlackButton
                  {...prevButton}
                  style={{
                    flex: 1,
                  }}>
                  {steps.length === 1
                    ? i18n('Refunds.Cancel.Button')
                    : i18n('Refunds.GoBack.Button')}
                </BlackButton>
                {currentStep !== 'selectRefundType' && (
                  <BlueButton
                    {...okButton}
                    style={{
                      flex: 2,
                    }}>
                    {currentStep === 'summary'
                      ? i18n('Refunds.Refund.Button')
                      : i18n('Refunds.ViewSummary.Button')}
                  </BlueButton>
                )}
              </Box>
            )
          )}
        </Box>
      </InnerWrapper>
    </Wrapper>
  );
};

export default Refunds;
